import React from "react";
import { Box, Typography, Link, useMediaQuery } from "@material-ui/core";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PhoneIcon from "@material-ui/icons/Phone";
import EmailIcon from "@material-ui/icons/Email";

const ABOUT_COPY =
  "Montestory is a thoughtfully designed digital platform built exclusively for Montessori schools, rooted in the core principles of hands-on, child-centric learning. Our mission is to simplify and strengthen the way educators plan, deliver, and track Montessori education. The platform comes with preloaded Areas of Work, Activities, Exercises, and language materials, enabling teachers to focus more on meaningful classroom engagement rather than administrative effort. With intelligent activity planning tailored to each child, continuous progress tracking, and insightful analytics, Montestory empowers educators to make informed decisions for every learner. We are continuously evolving, with comprehensive lesson plans, assignments, and refresher resources under development to support consistency and excellence in Montessori education.";

const sectionTitleStyle = (isMobile) => ({
  fontWeight: 800,
  fontSize: isMobile ? "1.5rem" : "1.85rem",
  letterSpacing: "-0.02em",
  color: "rgb(15, 59, 98)",
  marginBottom: isMobile ? 16 : 20,
  textAlign: "center",
});

const bodyStyle = (isMobile) => ({
  fontSize: isMobile ? 16 : 18,
  lineHeight: 1.75,
  fontWeight: 500,
  color: "rgba(15, 59, 98, 0.88)",
  textAlign: "center",
  maxWidth: 920,
  margin: "0 auto",
});

/** Narrow column for icons so every contact row shares one vertical axis */
const CONTACT_ICON_COL = 44;

/** Shrink-wrap so the block sits in the horizontal center of the section (not stretched full width). */
const contactListWrap = {
  width: "fit-content",
  maxWidth: "min(520px, calc(100% - 8px))",
  marginLeft: "auto",
  marginRight: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  alignItems: "stretch",
};

const contactRow = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  width: "100%",
  gap: 12,
};

const contactIconCell = {
  flex: `0 0 ${CONTACT_ICON_COL}px`,
  width: CONTACT_ICON_COL,
  display: "flex",
  justifyContent: "center",
  flexShrink: 0,
  paddingTop: 2,
};

const contactTextCell = {
  flex: 1,
  minWidth: 0,
  textAlign: "left",
};

const HomeAboutContact = () => {
  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <Box
      component="section"
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: isMobile ? "28px 18px 32px" : "44px 40px 48px",
        background: "linear-gradient(165deg, #e8f1fb 0%, #f7f9fc 42%, #ffffff 100%)",
        borderBottom: "1px solid rgba(15, 59, 98, 0.08)",
      }}
    >
      <Box style={{ width: "100%", maxWidth: 1100, margin: "0 auto" }}>
        <Box style={{ textAlign: "center", marginBottom: isMobile ? 8 : 12 }}>
          <img
            src="https://artistimages.blob.core.windows.net/montestory/logo 2.svg"
            alt="Montestory"
            style={{ height: isMobile ? 44 : 52, width: "auto" }}
          />
        </Box>

        {/* Contact Us first (order switched from typical About-then-Contact) */}
        <Box
          id="contact-us"
          style={{
            marginBottom: isMobile ? 36 : 44,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            component="h2"
            variant="h5"
            style={{ ...sectionTitleStyle(isMobile), width: "100%" }}
          >
            Contact Us
          </Typography>

          <Box style={contactListWrap}>
            <Box style={contactRow}>
              <Box style={contactIconCell}>
                <LocationOnIcon style={{ color: "rgb(25, 118, 210)", fontSize: 26 }} />
              </Box>
              <Box style={contactTextCell}>
                <Typography
                  style={{
                    fontSize: isMobile ? 16 : 17,
                    lineHeight: 1.75,
                    fontWeight: 600,
                    color: "rgba(15, 59, 98, 0.9)",
                    margin: 0,
                  }}
                >
                  E-212 Jains Carlton Creek (Corporate office)
                  <br />
                  Khajaguda
                  <br />
                  Hyderabad – 500089
                </Typography>
              </Box>
            </Box>

            <Box style={contactRow}>
              <Box style={contactIconCell}>
                <PhoneIcon style={{ color: "rgb(25, 118, 210)", fontSize: 24 }} />
              </Box>
              <Box style={contactTextCell}>
                <Typography style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, marginBottom: 6 }}>
                  <Link href="tel:+919848055562" color="inherit" style={{ textDecoration: "none" }}>
                    98480 555 62
                  </Link>
                </Typography>
                <Typography style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, margin: 0 }}>
                  <Link href="tel:+918247625893" color="inherit" style={{ textDecoration: "none" }}>
                    8247625893
                  </Link>
                </Typography>
              </Box>
            </Box>

            <Box style={contactRow}>
              <Box style={{ ...contactIconCell, paddingTop: 4 }}>
                <EmailIcon style={{ color: "rgb(25, 118, 210)", fontSize: 24 }} />
              </Box>
              <Box style={contactTextCell}>
                <Typography style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, margin: 0 }}>
                  <Link href="mailto:support@montestory.in" color="primary" style={{ fontWeight: 700 }}>
                    support@montestory.in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          style={{
            height: 1,
            maxWidth: 480,
            margin: "0 auto 28px",
            background: "linear-gradient(90deg, transparent, rgba(15, 59, 98, 0.15), transparent)",
          }}
        />

        <Box id="about-us">
          <Typography
            component="h2"
            variant="h5"
            style={sectionTitleStyle(isMobile)}
          >
            About Us
          </Typography>
          <Typography component="p" variant="body1" style={bodyStyle(isMobile)}>
            {ABOUT_COPY}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeAboutContact;
